import { memo, useEffect, useRef, useState } from 'react'
import { importLibrary } from '@googlemaps/js-api-loader'
import { AlertTriangle, Crosshair, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

import { realtimeConfig } from '../../config'
import { SHOW_WORKER_RESPONSE_STATUS } from '../../schedulingRules'
import type { Job, Worker } from '../../types'
import { formatDistance, type LatLng } from '../../utils/distance'
import type { JobDisplayStatus } from '../../utils/job-status'
import { isActiveResponse } from '../../utils/job-workers'
import { MISSING_REASON_TEXT, type FocusWorker, type SelectionFocus } from '../../utils/map-fit'
import { formatCompactAgo, formatCompactDuration } from '../../utils/time'
import { getInitials, getWorkerResponseVisual, WORKER_PIN_TONE, type WorkerPinTone } from '../status-meta'
import { MapHtmlMarker } from './MapHtmlMarker'

/** Small non-blocking label under a pin (AdvancedMarker/OverlayView content, no popups). */
function MapTag({ children, tone = 'default' }: { children: string; tone?: 'default' | 'alert' | 'candidate' }) {
  return (
    <span
      className={cn(
        'pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10px] font-semibold shadow',
        tone === 'alert' ? 'bg-red-600 text-white' : tone === 'candidate' ? 'bg-white/95 text-blue-800 ring-1 ring-blue-200' : 'bg-white/95 text-[#20352b]',
      )}
    >
      {children}
    </span>
  )
}

/**
 * Pin tone for an assigned worker. Without the response UI every assigned worker is green;
 * with it: response first, then late job / offline overrides.
 */
export function resolveWorkerPinTone(entry: FocusWorker, jobIsLate: boolean): WorkerPinTone {
  if (!SHOW_WORKER_RESPONSE_STATUS) return 'accepted'
  const visual = getWorkerResponseVisual(entry.jobWorker, entry.response)
  if (visual.tone === 'accepted' && entry.worker.status === 'offline') return 'inactive'
  if (visual.tone === 'accepted' && jobIsLate && entry.jobWorker.status !== 'started' && entry.jobWorker.status !== 'completed') return 'alert'
  return visual.tone
}

/** "Late 1h 8m", "Unassigned · Late 12m", "Overdue 14d", "Scheduled". */
export function describeJobTag(display: JobDisplayStatus, lateBy: string | null): string {
  if (display.kind === 'late') return lateBy ?? display.label
  return lateBy ? `${display.label} · ${lateBy}` : display.label
}

export const SelectedJobPin = memo(function SelectedJobPin({
  job,
  number,
  display,
  showTag,
  animate,
}: {
  job: Job
  number: number
  display: JobDisplayStatus
  showTag: boolean
  animate: boolean
}) {
  const lateBy = display.lateMinutes != null && display.lateMinutes > 0 ? `Late ${formatCompactDuration(display.lateMinutes)}` : null
  const late = display.kind === 'late' || display.kind === 'overdue' || Boolean(lateBy)
  const color = late ? '#dc2626' : '#f59e0b'
  const tag = describeJobTag(display, lateBy)
  return (
    <div className={cn('relative', animate && 'rt-bounce-once')} role="img" aria-label={`Selected job ${job.bookingNo}, ${tag}`}>
      {animate ? (
        <span className="pointer-events-none absolute bottom-0 left-1/2 h-4 w-9 -translate-x-1/2 translate-y-1/2 rounded-full opacity-40 motion-safe:animate-ping" style={{ background: color }} aria-hidden="true" />
      ) : null}
      <svg viewBox="0 0 32 40" width={46} height={57} className="drop-shadow-lg" aria-hidden="true">
        <path d="M16 0C7.2 0 0 7.1 0 15.9 0 27.8 16 40 16 40s16-12.2 16-24.1C32 7.1 24.8 0 16 0z" fill={color} stroke="white" strokeWidth={2.5} />
      </svg>
      <span className="absolute inset-x-0 top-[9px] text-center text-[14px] font-bold text-white" aria-hidden="true">{number || '•'}</span>
      {showTag ? <MapTag tone={late ? 'alert' : 'default'}>{tag}</MapTag> : null}
    </div>
  )
})

export const AssignedWorkerPin = memo(function AssignedWorkerPin({
  entry,
  tone,
  distanceKm,
  showTag,
  isHovered,
  animate,
  now,
  onHover,
  onActivate,
}: {
  entry: FocusWorker
  tone: WorkerPinTone
  distanceKm: number | null
  showTag: boolean
  isHovered: boolean
  animate: boolean
  now: number
  onHover: (workerId: string | null) => void
  onActivate: (workerId: string) => void
}) {
  const { worker, jobWorker, response } = entry
  const responseLabel = SHOW_WORKER_RESPONSE_STATUS ? getWorkerResponseVisual(jobWorker, response).label : null
  const colors = WORKER_PIN_TONE[tone]
  const firstName = worker.name.split(/\s+/)[0] ?? worker.name
  const seen = worker.locationSource && worker.locationSource !== 'live' ? formatCompactAgo(worker.lastSeenAt, now) : null
  const tag = [firstName, responseLabel, seen ? `seen ${seen}` : null].filter(Boolean).join(' · ')

  return (
    <div className="relative" style={{ transform: 'translateY(50%)' }}>
      {isHovered && animate ? (
        <span className={cn('pointer-events-none absolute -inset-2 rounded-full opacity-50 motion-safe:animate-ping', colors.solid)} aria-hidden="true" />
      ) : null}
      <button
        type="button"
        onMouseEnter={() => onHover(worker.id)}
        onMouseLeave={() => onHover(null)}
        onFocus={() => onHover(worker.id)}
        onBlur={() => onHover(null)}
        onClick={(event) => {
          event.stopPropagation()
          onActivate(worker.id)
        }}
        aria-label={`Assigned: ${worker.name}${responseLabel ? `, ${responseLabel}` : ''}${distanceKm != null ? `, ${formatDistance(distanceKm)} from the job` : ''}${seen ? `, last seen ${seen}` : ''}. Show in job card`}
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-full border-2 border-white text-[11px] font-bold text-white shadow-lg transition-transform',
          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#169b62]/60',
          colors.solid,
          isHovered && 'scale-125 ring-4',
          isHovered && colors.ring,
          tone === 'inactive' && 'border-dashed',
        )}
      >
        {getInitials(worker.name)}
      </button>
      {showTag ? <MapTag tone={tone === 'alert' ? 'alert' : 'default'}>{tag}</MapTag> : null}
    </div>
  )
})

/** Nearby worker who could be added: blue hollow pin with initials. Click = find in the strip. */
export const CandidatePin = memo(function CandidatePin({
  worker,
  distanceKm,
  showTag,
  isHovered,
  isFocused,
  animate,
  onHover,
  onActivate,
}: {
  worker: Worker
  distanceKm: number
  showTag: boolean
  isHovered: boolean
  isFocused: boolean
  animate: boolean
  onHover: (workerId: string | null) => void
  onActivate: (workerId: string) => void
}) {
  const firstName = worker.name.split(/\s+/)[0] ?? worker.name
  const active = isHovered || isFocused
  return (
    <div className="relative" style={{ transform: 'translateY(50%)' }}>
      {active && animate ? (
        <span className="pointer-events-none absolute -inset-1.5 rounded-full bg-blue-500/40 motion-safe:animate-ping" aria-hidden="true" />
      ) : null}
      <button
        type="button"
        onMouseEnter={() => onHover(worker.id)}
        onMouseLeave={() => onHover(null)}
        onFocus={() => onHover(worker.id)}
        onBlur={() => onHover(null)}
        onClick={(event) => {
          event.stopPropagation()
          onActivate(worker.id)
        }}
        aria-label={`Nearby worker ${worker.name}, ${formatDistance(distanceKm)} away. Show in nearby workers`}
        className={cn(
          'relative flex h-8 w-8 items-center justify-center rounded-full border-[2.5px] border-blue-600 text-[10px] font-bold shadow-md transition-transform',
          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-400/60',
          active ? 'scale-125 bg-blue-600 text-white' : 'bg-white text-blue-700',
        )}
      >
        {getInitials(worker.name)}
      </button>
      {showTag || active ? <MapTag tone="candidate">{`${firstName} · ${formatDistance(distanceKm)}`}</MapTag> : null}
    </div>
  )
})

/** Dashed line managed imperatively (updates its path in place as the worker moves). */
export function RouteLine({ map, path, color, dash = 'dotted' }: { map: google.maps.Map; path: LatLng[]; color: string; dash?: 'dotted' | 'dashed' }) {
  const lineRef = useRef<google.maps.Polyline | null>(null)

  useEffect(() => {
    const symbol = { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: color, scale: 3, ...(dash === 'dashed' ? { strokeWeight: 3 } : {}) }
    const line = new google.maps.Polyline({
      map,
      clickable: false,
      strokeOpacity: 0,
      zIndex: 50,
      icons: [{ icon: symbol, offset: '0', repeat: dash === 'dashed' ? '16px' : '12px' }],
    })
    lineRef.current = line
    return () => {
      line.setMap(null)
      lineRef.current = null
    }
  }, [color, dash, map])

  const pathKey = path.map((point) => `${point.lat},${point.lng}`).join('|')
  const pathRef = useRef(path)
  pathRef.current = path
  useEffect(() => {
    lineRef.current?.setPath(pathRef.current)
  }, [pathKey])

  return null
}

interface RouteEstimate {
  path: LatLng[]
  distanceKm: number
  etaMinutes: number
  source: 'directions' | 'straight'
}

/**
 * Driving route via Google Directions when VITE_REALTIME_USE_DIRECTIONS=true; otherwise (or on
 * any error) a straight line with a Haversine distance. Keyed on ~100 m rounding so GPS drift
 * does not trigger a new Directions call.
 */
function useRouteEstimate(route: FocusWorker['route']): RouteEstimate | null {
  const [directions, setDirections] = useState<{ key: string; estimate: RouteEstimate } | null>(null)
  const key = route ? [route.from.lat, route.from.lng, route.to.lat, route.to.lng].map((value) => value.toFixed(3)).join(',') : ''
  const routeRef = useRef(route)
  routeRef.current = route

  useEffect(() => {
    const current = routeRef.current
    if (!key || !current || !realtimeConfig.useDirections) return undefined
    let cancelled = false
    importLibrary('routes')
      .then(() => new google.maps.DirectionsService().route({ origin: current.from, destination: current.to, travelMode: google.maps.TravelMode.DRIVING }))
      .then((result) => {
        const leg = result.routes[0]?.legs[0]
        if (cancelled || !leg?.distance || !leg.duration) return
        setDirections({
          key,
          estimate: {
            path: result.routes[0].overview_path.map((point) => ({ lat: point.lat(), lng: point.lng() })),
            distanceKm: leg.distance.value / 1000,
            etaMinutes: Math.max(1, Math.round(leg.duration.value / 60)),
            source: 'directions',
          },
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [key])

  if (!route) return null
  if (directions && directions.key === key) return directions.estimate
  return { path: [route.from, route.to], distanceKm: route.distanceKm, etaMinutes: route.etaMinutes, source: 'straight' }
}

/** Dashed worker -> job line coloured by the worker's status, with a distance/ETA label. */
export function WorkerRoute({
  map,
  entry,
  tone,
  useAdvanced,
  animate,
}: {
  map: google.maps.Map
  entry: FocusWorker
  tone: WorkerPinTone
  useAdvanced: boolean
  animate: boolean
}) {
  const estimate = useRouteEstimate(entry.route)
  if (!entry.route || !estimate) return null
  const color = WORKER_PIN_TONE[tone].hex
  return (
    <>
      <RouteLine map={map} path={estimate.path} color={color} dash="dashed" />
      <MapHtmlMarker
        map={map}
        lat={(entry.route.from.lat + entry.route.to.lat) / 2}
        lng={(entry.route.from.lng + entry.route.to.lng) / 2}
        zIndex={60}
        animate={animate}
        useAdvanced={useAdvanced}
      >
        <span className="pointer-events-none block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow" style={{ background: color }}>
          {formatDistance(estimate.distanceKm)} · ~{estimate.etaMinutes} min{estimate.source === 'directions' ? ' by road' : ''}
        </span>
      </MapHtmlMarker>
    </>
  )
}

/**
 * Compact banner for missing locations, above the nearby strip (the popup owns the top-left).
 * Job: "Location not available" + Locate (on-demand geocoding). Worker: "Worker location unavailable".
 */
export function MissingLocationBanner({
  focus,
  geocodeState,
  onLocate,
  className,
}: {
  focus: SelectionFocus
  geocodeState: 'pending' | 'failed' | null
  onLocate: () => void
  className?: string
}) {
  const jobReason = focus.missing.find((reason) => reason.startsWith('job_'))
  const unlocated = focus.workers.filter((entry) => !entry.located && isActiveResponse(entry.response)).map((entry) => entry.worker.name)
  if (!jobReason && unlocated.length === 0) return null
  return (
    <div
      role="status"
      onClick={(event) => event.stopPropagation()}
      className={cn(
        'absolute left-1/2 z-20 flex max-w-[calc(100%-1.5rem)] -translate-x-1/2 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/95 px-2.5 py-1.5 text-[11px] text-amber-900 shadow',
        className,
      )}
    >
      {geocodeState === 'pending'
        ? <Loader2 className="h-3.5 w-3.5 shrink-0 motion-safe:animate-spin" aria-hidden="true" />
        : <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      <span className="min-w-0">
        {jobReason ? <><strong>Location not available for this job.</strong> {MISSING_REASON_TEXT[jobReason]}</> : null}
        {jobReason && unlocated.length ? ' ' : null}
        {unlocated.length ? <><strong>Worker location unavailable</strong> ({unlocated.join(', ')})</> : null}
      </span>
      {jobReason && focus.job.address && jobReason !== 'job_geocoding' ? (
        <button
          type="button"
          onClick={onLocate}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-300 bg-white px-1.5 py-0.5 font-semibold hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        >
          <Crosshair className="h-3 w-3" aria-hidden="true" />
          {jobReason === 'job_geocode_failed' ? 'Try again' : 'Locate'}
        </button>
      ) : null}
    </div>
  )
}
